'use client'

import { useState, useEffect } from 'react'
import { ChevronLeft, ChevronRight, ShoppingBag } from 'lucide-react'
import { CAROUSEL_SLIDES } from '@/mock/seeds/carousel'
import { Button } from '@/components/ui/button'

export default function HeroCarousel() {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [currentSideBanner, setCurrentSideBanner] = useState(0)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const mainSlides = CAROUSEL_SLIDES.slice(0, 5) // Main carousel slides (5 slides)
  const sideBanners = CAROUSEL_SLIDES.slice(5) // Side banners (remaining slides)

  useEffect(() => {
    const timer = setInterval(() => {
      setIsTransitioning(true)
      setTimeout(() => {
        setCurrentSlide((prev) => (prev + 1) % mainSlides.length)
        setIsTransitioning(false)
      }, 300)
    }, 6000)

    return () => clearInterval(timer)
  }, [mainSlides.length])

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSideBanner((prev) => (prev + 1) % sideBanners.length)
    }, 5000) // Slower interval - 5 seconds

    return () => clearInterval(timer)
  }, [sideBanners.length])

  const goToSlide = (index: number) => {
    setIsTransitioning(true)
    setTimeout(() => {
      setCurrentSlide(index)
      setIsTransitioning(false)
    }, 300)
  }

  const goToPrevious = () => {
    setIsTransitioning(true)
    setTimeout(() => {
      setCurrentSlide((prev) => (prev - 1 + mainSlides.length) % mainSlides.length)
      setIsTransitioning(false)
    }, 300)
  }

  const goToNext = () => {
    setIsTransitioning(true)
    setTimeout(() => {
      setCurrentSlide((prev) => (prev + 1) % mainSlides.length)
      setIsTransitioning(false)
    }, 300)
  }

  const currentSlideData = mainSlides[currentSlide]

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">
      {/* Main Carousel - Takes 8/12 width on desktop */}
      <div className="lg:col-span-8 relative w-full h-[300px] sm:h-[400px] lg:h-[450px] overflow-hidden rounded-xl shadow-2xl group">
        {/* Background Image with Ken Burns Effect */}
        <div
          className={`absolute inset-0 transition-all duration-[6000ms] ease-linear ${
            isTransitioning ? 'scale-100' : 'scale-110'
          }`}
        >
          <img
            src={currentSlideData.image_url}
            alt={currentSlideData.title}
            className="w-full h-full object-cover hidden md:block"
          />
          <img
            src={currentSlideData.mobile_image_url}
            alt={currentSlideData.title}
            className="w-full h-full object-cover md:hidden"
          />
        </div>

        {/* Gradient Overlay */}
        <div 
          className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent"
          style={{ 
            background: `linear-gradient(to right, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.5) 50%, transparent 100%)`
          }}
        />

        {/* Content */}
        <div className="absolute inset-0 flex items-center">
          <div className="container mx-auto px-6 lg:px-12">
            <div className="max-w-xl space-y-4 animate-in fade-in slide-in-from-left duration-700">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-accent/90 backdrop-blur-sm rounded-full text-accent-foreground text-sm font-semibold">
                <ShoppingBag className="h-4 w-4" />
                {currentSlideData.cta_text || 'Limited Offer'}
              </div>

              {/* Title */}
              <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-tight drop-shadow-lg">
                {currentSlideData.title}
              </h2>

              {/* Subtitle */}
              <p className="text-lg sm:text-xl lg:text-2xl text-white/90 font-medium drop-shadow-md">
                {currentSlideData.subtitle}
              </p>

              {/* CTA Button */}
              <div className="flex gap-3 pt-2">
                <Button
                  size="lg"
                  className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold px-8 shadow-lg hover:shadow-xl transition-all hover:scale-105"
                  onClick={() => currentSlideData.cta_link && window.open(currentSlideData.cta_link, '_self')}
                >
                  {currentSlideData.cta_text || 'Shop Now'}
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="bg-white/10 hover:bg-white/20 text-white border-white/30 backdrop-blur-sm font-semibold"
                  onClick={() => currentSlideData.cta_link && window.open(currentSlideData.cta_link, '_self')}
                >
                  Learn More
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={goToPrevious}
          className="absolute left-4 lg:left-6 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-900 rounded-full p-2.5 lg:p-3 transition-all shadow-xl opacity-0 group-hover:opacity-100 hover:scale-110"
          aria-label="Previous slide"
        >
          <ChevronLeft className="h-5 w-5 lg:h-6 lg:w-6" />
        </button>
        <button
          onClick={goToNext}
          className="absolute right-4 lg:right-6 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-900 rounded-full p-2.5 lg:p-3 transition-all shadow-xl opacity-0 group-hover:opacity-100 hover:scale-110"
          aria-label="Next slide"
        >
          <ChevronRight className="h-5 w-5 lg:h-6 lg:w-6" />
        </button>

        {/* Pagination Dots */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2.5 bg-black/30 backdrop-blur-sm px-4 py-2.5 rounded-full">
          {mainSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`rounded-full transition-all duration-300 ${
                index === currentSlide
                  ? 'bg-white w-8 h-2.5'
                  : 'bg-white/50 hover:bg-white/75 w-2.5 h-2.5'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Side Banners - Takes 4/12 width on desktop, stacked vertically */}
      <div className="hidden lg:flex flex-col gap-4 lg:col-span-4 h-[450px]">
        {/* Top Side Banner - Rotating */}
        <div className="relative flex-1 overflow-hidden rounded-xl shadow-lg max-h-[calc(50%-0.5rem)]">
          <div 
            className="flex h-full transition-transform duration-700 ease-in-out"
            style={{ transform: `translateX(-${(currentSideBanner % sideBanners.length) * 100}%)` }}
          >
            {sideBanners.map((banner, idx) => (
              <div
                key={banner.slide_id}
                className="min-w-full h-full relative cursor-pointer group"
                style={{ backgroundColor: banner.background_color }}
                onClick={() => banner.cta_link && window.open(banner.cta_link, '_self')}
              >
                <img
                  src={banner.image_url}
                  alt={banner.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent flex items-end p-5">
                  <div className="text-left text-white w-full">
                    <h3 className="text-lg xl:text-xl font-bold mb-1 drop-shadow-lg">{banner.title}</h3>
                    <p className="text-sm text-white/90 mb-3 drop-shadow-md">{banner.subtitle}</p>
                    <div className="inline-flex items-center gap-1 text-sm font-semibold text-white group-hover:gap-2 transition-all">
                      Explore Now
                      <ChevronRight className="h-4 w-4" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Side Banner - Rotating (offset by 1) */}
        <div className="relative flex-1 overflow-hidden rounded-xl shadow-lg max-h-[calc(50%-0.5rem)]">
          <div 
            className="flex h-full transition-transform duration-700 ease-in-out"
            style={{ transform: `translateX(-${((currentSideBanner + 1) % sideBanners.length) * 100}%)` }}
          >
            {sideBanners.map((banner, idx) => (
              <div
                key={banner.slide_id}
                className="min-w-full h-full relative cursor-pointer group"
                style={{ backgroundColor: banner.background_color }}
                onClick={() => banner.cta_link && window.open(banner.cta_link, '_self')}
              >
                <img
                  src={banner.image_url}
                  alt={banner.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent flex items-end p-5">
                  <div className="text-left text-white w-full">
                    <h3 className="text-lg xl:text-xl font-bold mb-1 drop-shadow-lg">{banner.title}</h3>
                    <p className="text-sm text-white/90 mb-3 drop-shadow-md">{banner.subtitle}</p>
                    <div className="inline-flex items-center gap-1 text-sm font-semibold text-white group-hover:gap-2 transition-all">
                      Explore Now
                      <ChevronRight className="h-4 w-4" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
